import { useCallback, useState } from 'react';
import {
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  Selection,
} from '@fluentui/react';
import moment from 'moment';
import { DetailsListDefault } from '../../../components/parts';
import { BillitInvoice } from '../types';
import { BillitPaymentLabel, BillitStatusLabel } from '../billitDetailUi';

interface Props {
  items: BillitInvoice[];
  enableShimmer?: boolean;
  onSelect?: (invoice: BillitInvoice) => void;
  loadMore?: boolean;
  loadMoreCallback?: () => void;
}

const buildColumns = (): IColumn[] => {
  const parseJsonField = (jsonString: string | null) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  };

  const getAssigneeName = (invoice: BillitInvoice) => {
    const emp = invoice.assigned_employee;
    if (!emp) return '-';
    return [emp.first_name, emp.last_name].filter(Boolean).join(' ') || '-';
  };

  const getSupplierName = (invoice: BillitInvoice) => {
    const supplier = parseJsonField(invoice.supplier_json);
    if (supplier?.Name) return supplier.Name;
    return '-';
  };

  return [
    {
      key: 'column1',
      name: 'Factuurnummer',
      fieldName: 'order_number',
      minWidth: 70,
      maxWidth: 75,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => (
        <span>{invoice.order_number || '-'}</span>
      ),
      data: 'string',
      isPadded: true,
    },
    {
      key: 'column2',
      name: 'Datum',
      fieldName: 'order_date',
      minWidth: 78,
      maxWidth: 80,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => (
        <span>
          {invoice.order_date
            ? moment(invoice.order_date).format('DD/MM/YYYY')
            : '-'}
        </span>
      ),
      data: 'string',
      isPadded: true,
    },
    {
      key: 'columnStatus',
      name: 'Status',
      fieldName: 'order_status',
      minWidth: 158,
      maxWidth: 160,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => (
        <BillitStatusLabel status={invoice.order_status} uniformSize />
      ),
      data: 'string',
      isPadded: true,
    },
    {
      key: 'columnPayment',
      name: 'Betaling',
      fieldName: 'paid',
      minWidth: 110,
      maxWidth: 130,
      isResizable: true,
      onRender: (invoice: BillitInvoice) => <BillitPaymentLabel invoice={invoice} />,
      data: 'string',
      isPadded: true,
    },
    {
      key: 'columnSent',
      name: 'Verstuurd',
      fieldName: 'sent',
      minWidth: 70,
      maxWidth: 80,
      isResizable: true,
      onRender: (invoice: BillitInvoice) => (
        <span>{invoice.sent ? 'Ja' : 'Nee'}</span>
      ),
      data: 'string',
      isPadded: true,
    },
    {
      key: 'columnAssignee',
      name: 'Toegewezen aan',
      fieldName: 'assigned_employee',
      minWidth: 95,
      maxWidth: 115,
      isResizable: true,
      isRowHeader: true,
      onRender: (inv: BillitInvoice) => <span>{getAssigneeName(inv)}</span>,
      data: 'string',
      isPadded: true,
    },
    {
      key: 'column3',
      name: 'Leverancier',
      fieldName: 'supplier',
      minWidth: 160,
      maxWidth: 240,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => <span>{getSupplierName(invoice)}</span>,
      data: 'string',
      isPadded: true,
    },
    {
      key: 'column4',
      name: 'Totaal (excl. BTW)',
      fieldName: 'total_excl',
      minWidth: 82,
      maxWidth: 88,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => (
        <span>
          {invoice.total_excl
            ? `${Number(invoice.total_excl).toFixed(2)} ${invoice.currency || 'EUR'}`
            : '-'}
        </span>
      ),
      data: 'string',
      isPadded: true,
    },
    {
      key: 'column5',
      name: 'Totaal (incl. BTW)',
      fieldName: 'total_incl',
      minWidth: 82,
      maxWidth: 88,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => (
        <span>
          {invoice.total_incl
            ? `${Number(invoice.total_incl).toFixed(2)} ${invoice.currency || 'EUR'}`
            : '-'}
        </span>
      ),
      data: 'string',
      isPadded: true,
    },
    {
      key: 'column6',
      name: 'Achterstallig',
      fieldName: 'overdue',
      minWidth: 68,
      maxWidth: 78,
      isResizable: true,
      isRowHeader: true,
      onRender: (invoice: BillitInvoice) => (
        <span>
          {invoice.overdue
            ? `Ja${invoice.days_overdue ? ` (${invoice.days_overdue} dagen)` : ''}`
            : 'Nee'}
        </span>
      ),
      data: 'string',
      isPadded: true,
    },
  ];
};

const BillitInvoiceDetailList = ({
  items,
  enableShimmer = false,
  onSelect,
  loadMore = false,
  loadMoreCallback = () => {},
}: Props) => {
  const [columns, setColumns] = useState<IColumn[]>(buildColumns);

  const onColumnResize = useCallback((column?: IColumn, newWidth?: number) => {
    const colKey = column?.key;
    if (!colKey || newWidth == null) return;
    setColumns(prev =>
      prev.map(col => {
        if (col.key !== colKey) return col;
        return { ...col, maxWidth: Math.max(col.minWidth, newWidth) };
      }));
  }, []);

  const selection = new Selection({
    onSelectionChanged: () => {
      const selectedItem = selection.getSelection()[0] as BillitInvoice;
      if (selectedItem && onSelect) {
        onSelect(selectedItem);
      }
    },
  });

  return (
    <DetailsListDefault
      items={items}
      columns={columns}
      layoutMode={DetailsListLayoutMode.fixedColumns}
      selection={selection}
      selectionMode={SelectionMode.single}
      enableShimmer={enableShimmer}
      loadMore={loadMore}
      lazyLoading={loadMore}
      loadMoreCallback={loadMoreCallback}
      onColumnResize={onColumnResize}
    />
  );
};

export default BillitInvoiceDetailList;
